'use client'

import React, { createContext, useContext, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useSession } from 'next-auth/react'
import { getSidebarNavItems } from '@/config/navigation'
import { useActiveRole } from '@/lib/active-role'
import { RoleBadge } from './RoleBadge'
import { BrandLogo } from '@/components/brand'
import { useBranding } from '@/context/BrandingContext'

interface SidebarContextType {
  isCollapsed: boolean
  setIsCollapsed: (collapsed: boolean) => void
  toggle: () => void
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined)

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const toggle = () => setIsCollapsed(!isCollapsed)

  return (
    <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed, toggle }}>
      {children}
    </SidebarContext.Provider>
  )
}

function PoweredBy({ isCollapsed }: { isCollapsed: boolean }) {
  if (isCollapsed) {
    return <img src='/supervity-favicon.png' alt='Powered by Supervity' className='mx-auto h-6 w-6 object-contain' />
  }
  return (
    <div className='flex items-center justify-center gap-2 rounded-md border border-border-default bg-background-subtle px-3 py-2.5'>
      <span className='text-xs text-text-tertiary'>Powered by</span>
      <img src='/supervity-logo.png' alt='Supervity' className='h-5 w-auto object-contain' />
    </div>
  )
}

export function Sidebar() {
  const { branding } = useBranding()
  const { isCollapsed, setIsCollapsed } = useSidebar()
  const { data: session } = useSession()
  const pathname = usePathname()
  const { activeRole } = useActiveRole()
  const navItems = getSidebarNavItems(session?.roles, activeRole)

  /** The longest nav link that matches the path wins, so /spend does not light up on /spend/tracking. */
  const activeHref = navItems
    .flatMap((i) => (i.children ? i.children.map((c) => c.href) : [i.href]))
    .filter((h): h is string => !!h && (h === '/' ? pathname === '/' : pathname === h || pathname.startsWith(h + '/')))
    .sort((a, b) => b.length - a.length)[0]
  const isActiveHref = (href?: string) => !!href && href === activeHref

  const saveExpandedToStorage = (state: Record<string, boolean>) => {
    try { localStorage.setItem('sidebar-expanded-sections', JSON.stringify(state)) } catch {}
  }

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem('sidebar-expanded-sections')
      if (stored) return JSON.parse(stored)
    } catch {}
    return {}
  })

  const toggleSection = (label: string) => {
    setExpandedSections((prev) => {
      const next = { ...prev, [label]: !prev[label] }
      saveExpandedToStorage(next)
      return next
    })
  }

  React.useEffect(() => {
    setExpandedSections((prev) => {
      const merged: Record<string, boolean> = {}
      navItems.forEach((item) => {
        if (item.children) {
          merged[item.label] = prev[item.label] ?? false
        }
      })
      return merged
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.roles, activeRole])

  React.useEffect(() => {
    const owner = navItems.find((i) => i.children?.some((c) => c.href === activeHref))
    if (owner) {
      setExpandedSections((prev) => {
        if (prev[owner.label]) return prev
        const next = { ...prev, [owner.label]: true }
        saveExpandedToStorage(next)
        return next
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeHref])

  return (
    <TooltipProvider>
      <aside
        role='navigation'
        aria-label='Main navigation'
        className={cn(
          'sidebar-shell hidden flex-col md:flex',
          'fixed bottom-0 left-0 top-0 z-fixed',
          'border-r border-border-default bg-white',
          'transition-all duration-300 ease-out',
          isCollapsed ? 'w-[72px]' : 'w-[260px]'
        )}
      >
        <div
          className={cn(
            'flex h-16 items-center px-4',
            'border-b border-border-default',
            isCollapsed && 'justify-center px-2'
          )}
        >
          <Link href='/' className='group flex items-center gap-2'>
            {isCollapsed ? (
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  {branding.logoSrc ? (
                    <BrandLogo
                      variant='mark'
                      className='h-8 w-8 object-contain transition-transform duration-200 group-hover:scale-105'
                    />
                  ) : (
                    <span
                      aria-label={branding.companyName}
                      className='flex h-8 w-8 items-center justify-center rounded-lg bg-brand-navy text-sm font-semibold text-white'
                    >
                      {branding.companyName.charAt(0).toUpperCase()}
                    </span>
                  )}
                </TooltipTrigger>
                <TooltipContent side='right'>
                  {branding.companyName}
                </TooltipContent>
              </Tooltip>
            ) : (
              <>
                <BrandLogo
                  className='h-10 w-auto max-w-[9rem] shrink-0 object-contain transition-transform duration-200 group-hover:scale-105'
                />
                {!branding.logoSrc && (
                  <div className='flex flex-col leading-tight'>
                    <span className='text-sm font-semibold leading-tight text-brand-navy'>
                      {branding.companyName}
                    </span>
                  </div>
                )}
              </>
            )}
          </Link>
        </div>

        <nav
          aria-label='Primary navigation'
          className='scrollbar-hide flex-1 overflow-y-auto px-3 py-4'
        >
          <ul className='space-y-1'>
            {navItems.map((item, idx) => {
              if (item.children) {
                const isExpanded = expandedSections[item.label]
                const sectionActive = item.children.some((c) => isActiveHref(c.href))
                return (
                  <li key={idx}>
                    <div>
                      {!isCollapsed && (
                        <button
                          onClick={() => toggleSection(item.label)}
                          className={cn(
                            'group mb-1 flex w-full items-center justify-between rounded-md px-3 py-1.5 transition-colors hover:text-brand-navy',
                            sectionActive ? 'text-brand-navy' : 'text-text-tertiary'
                          )}
                        >
                          <span className='text-[11px] font-semibold uppercase tracking-wider'>{item.label}</span>
                          <Icons.chevronDown
                            className={cn(
                              'h-3 w-3 opacity-60 transition-transform duration-200 group-hover:opacity-100',
                              isExpanded && 'rotate-180'
                            )}
                            strokeWidth={1.5}
                          />
                        </button>
                      )}
                      {isCollapsed ? (
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger asChild>
                            <div
                              className={cn(
                                'flex cursor-pointer justify-center rounded py-3 transition-colors',
                                sectionActive ? 'bg-brand-navy/5' : 'hover:bg-background-subtle'
                              )}
                            >
                              <item.icon
                                className={cn(
                                  'h-5 w-5',
                                  sectionActive ? 'text-brand-navy' : 'text-text-secondary'
                                )}
                                strokeWidth={1.5}
                              />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side='right'>{item.label}</TooltipContent>
                        </Tooltip>
                      ) : (
                        isExpanded && (
                          <div className='mb-3 space-y-0.5'>
                            {item.children.map((child) => {
                              const active = isActiveHref(child.href)
                              return (
                                <Link
                                  key={child.href}
                                  href={child.href}
                                  aria-current={active ? 'page' : undefined}
                                  className={cn(
                                    'relative flex items-center rounded-md py-2 pl-7 pr-3 text-sm transition-colors',
                                    active
                                      ? 'bg-slate-100 font-medium text-brand-navy'
                                      : 'text-text-secondary hover:bg-slate-50 hover:text-text-primary'
                                  )}
                                >
                                  {active && <span className='absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-brand-navy' />}
                                  {child.label}
                                </Link>
                              )
                            })}
                          </div>
                        )
                      )}
                    </div>
                  </li>
                )
              }

              const active = isActiveHref(item.href)
              return (
                <li key={idx}>
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <Link
                        href={item.href || '#'}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'relative flex h-9 w-full items-center gap-3 rounded-md px-3 text-sm transition-colors',
                          active
                            ? 'bg-brand-navy/[0.07] font-medium text-brand-navy'
                            : 'text-text-secondary hover:bg-background-subtle hover:text-text-primary',
                          isCollapsed && 'justify-center px-2'
                        )}
                      >
                        <item.icon
                          className={cn(
                            'h-5 w-5 shrink-0',
                            active && 'text-brand-navy'
                          )}
                          strokeWidth={1.5}
                        />
                        {!isCollapsed && <span>{item.label}</span>}
                      </Link>
                    </TooltipTrigger>
                    {isCollapsed && (
                      <TooltipContent side='right'>{item.label}</TooltipContent>
                    )}
                  </Tooltip>
                </li>
              )
            })}
          </ul>
        </nav>

        <div className='space-y-3 border-t border-border-default p-3'>
          <RoleBadge isCollapsed={isCollapsed} />
          <PoweredBy isCollapsed={isCollapsed} />
        </div>

        <div className='border-t border-border-default p-3'>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant='ghost'
                size={isCollapsed ? 'icon-sm' : 'small'}
                onClick={() => setIsCollapsed(!isCollapsed)}
                className={cn(
                  'w-full rounded-md font-medium text-text-secondary transition-colors hover:!bg-background-subtle hover:!text-brand-navy',
                  isCollapsed && 'px-2'
                )}
                aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {isCollapsed ? (
                  <Icons.panelOpen className='h-4 w-4' strokeWidth={1.5} />
                ) : (
                  <>
                    <Icons.panelClose className='mr-2 h-4 w-4' strokeWidth={1.5} />
                    <span className='text-xs'>Collapse</span>
                  </>
                )}
              </Button>
            </TooltipTrigger>
            {isCollapsed && (
              <TooltipContent side='right'>Expand sidebar</TooltipContent>
            )}
          </Tooltip>
        </div>
      </aside>
    </TooltipProvider>
  )
}
